<template>
  <div class="review-page">
    <section class="review-toolbar" aria-label="商品审核筛选">
      <div class="review-toolbar__summary">
        <div class="review-metrics" aria-label="审核数据概览">
          <div class="review-metric">
            <span>待处理</span>
            <strong>{{ reviewCounts.PENDING }}</strong>
          </div>
          <div class="review-metric">
            <span>有风险</span>
            <strong class="review-metric__risk">{{ riskReviewCount }}</strong>
          </div>
          <div class="review-metric">
            <span>全部</span>
            <strong>{{ reviews.length }}</strong>
          </div>
        </div>

        <div class="data-source" :class="{ 'data-source--demo': usingFallbackData }">
          <span class="data-source__dot"></span>
          <span>{{ usingFallbackData ? "演示数据" : "实时数据" }}</span>
        </div>
      </div>

      <div v-if="sourceMessage" class="source-notice" role="status">
        <strong>数据未接入</strong>
        <span>{{ sourceMessage }}</span>
      </div>

      <div class="filter-bar">
        <div class="status-tabs" aria-label="审核状态">
          <button
            v-for="tab in statusTabs"
            :key="tab.value || 'ALL'"
            type="button"
            class="status-tab"
            :class="{ 'status-tab--active': filters.status === tab.value }"
            :aria-pressed="String(filters.status === tab.value)"
            @click="setStatusFilter(tab.value)"
          >
            <span>{{ tab.label }}</span>
            <span class="status-tab__count">{{ tab.count }}</span>
          </button>
        </div>

        <label class="filter-control filter-control--category">
          <span class="sr-only">商品频道</span>
          <select v-model="filters.categoryCode" @change="applyFilters">
            <option value="">全部频道</option>
            <option v-for="item in categoryOptions" :key="item.code" :value="item.code">{{ item.name }}</option>
          </select>
        </label>

        <label class="filter-search">
          <span class="filter-search__icon" aria-hidden="true"></span>
          <input
            v-model.trim="filters.keyword"
            type="search"
            placeholder="搜索商品或商家"
            @input="applyFilters"
          />
        </label>

        <button class="refresh-button" type="button" :disabled="loading" @click="loadReviews">
          {{ loading ? "刷新中" : "刷新" }}
        </button>
      </div>
    </section>

    <section class="review-workbench">
      <aside class="queue-panel" aria-label="审核队列">
        <div class="queue-panel__head">
          <div>
            <div class="queue-panel__title">审核队列</div>
            <div class="queue-panel__desc">{{ queueSummary }}</div>
          </div>
          <span class="queue-panel__count">{{ filteredReviews.length }}</span>
        </div>

        <div v-if="loading" class="queue-state">正在加载审核记录...</div>

        <div v-else-if="filteredReviews.length" class="review-list">
          <button
            v-for="item in filteredReviews"
            :key="String(item.id)"
            type="button"
            class="review-item"
            :class="{ 'review-item--active': selectedReview && selectedReview.id === item.id }"
            @click="selectReview(item)"
          >
            <img class="review-item__image" :src="item.imageUrl" alt="" />
            <span class="review-item__content">
              <span class="review-item__topline">
                <strong class="review-item__title">{{ item.title }}</strong>
                <span class="status-dot" :class="resolveStatusDotClass(item.status)"></span>
              </span>
              <span class="review-item__store">{{ item.storeName }}</span>
              <span class="review-item__bottomline">
                <strong>{{ item.priceText }}</strong>
                <span>{{ item.categoryName }}</span>
                <span>{{ formatSubmittedAt(item.submittedAt) }}</span>
              </span>
              <span v-if="countRisks(item)" class="review-item__risk">{{ countRisks(item) }} 项需复核</span>
            </span>
          </button>
        </div>

        <div v-else class="queue-empty">
          <div class="queue-empty__mark">0</div>
          <strong>没有匹配的商品</strong>
          <span>调整状态、频道或搜索条件后再试。</span>
        </div>
      </aside>

      <article v-if="selectedReview" class="review-detail">
        <header class="detail-header">
          <div class="detail-header__copy">
            <div class="detail-header__eyebrow">商品 #{{ selectedReview.id }} · {{ selectedReview.storeName }}</div>
            <div class="detail-header__title-row">
              <h2>{{ selectedReview.title }}</h2>
              <span class="status-pill" :class="resolveStatusClass(selectedReview.status)">{{ selectedReview.statusLabel }}</span>
            </div>
          </div>
          <div class="detail-progress">
            <span>{{ selectedPassedCount }} 项通过</span>
            <strong v-if="selectedRiskCount">{{ selectedRiskCount }} 项待确认</strong>
            <strong v-else class="detail-progress__passed">检查项正常</strong>
          </div>
        </header>

        <div class="detail-scroll">
          <section class="product-summary">
            <img class="product-summary__image" :src="selectedReview.imageUrl" alt="" />
            <div class="product-summary__body">
              <div class="product-summary__line">
                <div class="product-summary__price">{{ selectedReview.priceText }}</div>
                <span>{{ selectedReview.deliveryLabel }}</span>
              </div>
              <p>{{ selectedReview.description }}</p>
              <div class="tag-row">
                <span v-for="tag in selectedReview.tags" :key="tag" class="tag-pill">{{ tag }}</span>
              </div>
            </div>
          </section>

          <div class="detail-columns">
            <div class="detail-content">
              <section class="detail-section">
                <div class="section-heading">
                  <div>
                    <h3>发布信息</h3>
                    <p>核对商品归属和前端展示字段</p>
                  </div>
                </div>
                <dl class="info-grid">
                  <div>
                    <dt>商家</dt>
                    <dd>{{ selectedReview.storeName }}</dd>
                  </div>
                  <div>
                    <dt>商品频道</dt>
                    <dd>{{ selectedReview.categoryName }}</dd>
                  </div>
                  <div>
                    <dt>提交时间</dt>
                    <dd>{{ selectedReview.submittedAt }}</dd>
                  </div>
                  <div>
                    <dt>履约方式</dt>
                    <dd>{{ selectedReview.deliveryLabel }}</dd>
                  </div>
                </dl>
              </section>

              <section class="detail-section detail-section--checks">
                <div class="section-heading">
                  <div>
                    <h3>自动检查</h3>
                    <p>风险项不会自动阻断，需要人工确认</p>
                  </div>
                  <span class="risk-summary" :class="{ 'risk-summary--passed': !selectedRiskCount }">
                    {{ selectedRiskCount ? selectedRiskCount + " 项风险" : "全部正常" }}
                  </span>
                </div>
                <div class="check-list">
                  <div v-for="item in selectedReview.reviewChecks" :key="item.label" class="check-row">
                    <span class="check-row__icon" :class="item.passed ? 'check-row__icon--passed' : 'check-row__icon--risk'">
                      {{ item.passed ? "✓" : "!" }}
                    </span>
                    <div>
                      <div class="check-row__title">{{ item.label }}</div>
                      <div class="check-row__desc">{{ item.desc }}</div>
                    </div>
                  </div>
                </div>
              </section>
            </div>

            <aside class="decision-panel">
              <div class="section-heading">
                <div>
                  <h3>审核结论</h3>
                  <p>备注会保留在审核记录中</p>
                </div>
              </div>

              <label class="decision-field">
                <span>审核备注 <small>选填</small></span>
                <textarea
                  v-model.trim="decisionForm.reviewNote"
                  placeholder="记录判断依据或补充说明"
                />
              </label>

              <label class="decision-field">
                <span>驳回原因 <small>驳回时必填</small></span>
                <textarea
                  v-model.trim="decisionForm.rejectReason"
                  class="decision-field__textarea--reject"
                  placeholder="填写商家需要修改的内容"
                />
              </label>

              <div class="reason-presets">
                <button
                  v-for="reason in rejectReasonPresets"
                  :key="reason"
                  type="button"
                  @click="applyRejectReason(reason)"
                >
                  {{ reason }}
                </button>
              </div>
            </aside>
          </div>
        </div>

        <footer class="decision-bar">
          <div class="decision-bar__state">
            <template v-if="actionState">
              <span class="decision-bar__indicator" :class="{ 'decision-bar__indicator--error': actionError }"></span>
              <span :class="{ 'decision-bar__text--error': actionError }">{{ actionState }}</span>
            </template>
            <template v-else>
              <span>审核后将自动切换到队列中的下一件商品</span>
            </template>
          </div>
          <div class="decision-bar__actions">
            <button
              class="decision-button decision-button--reject"
              type="button"
              :disabled="submitting || selectedReview.status === 'REJECTED'"
              @click="rejectCurrent"
            >
              {{ submitting && actionType === "reject" ? "提交中..." : "驳回商品" }}
            </button>
            <button
              class="decision-button decision-button--approve"
              type="button"
              :disabled="submitting || selectedReview.status === 'APPROVED'"
              @click="approveCurrent"
            >
              {{ submitting && actionType === "approve" ? "提交中..." : "通过审核" }}
            </button>
          </div>
        </footer>
      </article>

      <div v-else class="review-detail review-detail--empty">
        <div class="detail-empty__mark">审</div>
        <h2>选择一件商品开始审核</h2>
        <p>左侧队列会保留当前筛选条件。</p>
      </div>
    </section>
  </div>
</template>

<script>
import {
  approveProductReview,
  fetchProductReviews,
  rejectProductReview
} from "../api/productReview"

function createEmptyDecisionForm() {
  return {
    reviewNote: "",
    rejectReason: ""
  }
}

function fallbackCategoryOptions() {
  return [
    { code: "live-feed", name: "活饵" },
    { code: "supplement", name: "补剂" },
    { code: "equipment", name: "器材" },
    { code: "bedding", name: "垫材清洁" },
    { code: "bundle", name: "套餐" },
    { code: "live-consult", name: "活体咨询" }
  ]
}

export default {
  name: "ProductReviewView",
  data() {
    return {
      loading: false,
      submitting: false,
      actionType: "",
      actionState: "",
      actionError: false,
      usingFallbackData: false,
      sourceMessage: "",
      filters: {
        status: "PENDING",
        categoryCode: "",
        keyword: ""
      },
      categoryOptions: fallbackCategoryOptions(),
      reviews: [],
      filteredReviews: [],
      selectedReview: null,
      decisionForm: createEmptyDecisionForm(),
      rejectReasonPresets: ["主图不清晰", "规格信息不完整", "类目选择错误", "履约说明不完整"]
    }
  },
  computed: {
    reviewCounts() {
      return this.reviews.reduce((counts, item) => {
        counts[item.status] = (counts[item.status] || 0) + 1
        return counts
      }, { PENDING: 0, APPROVED: 0, REJECTED: 0 })
    },
    statusTabs() {
      return [
        { value: "PENDING", label: "待审核", count: this.reviewCounts.PENDING },
        { value: "APPROVED", label: "已通过", count: this.reviewCounts.APPROVED },
        { value: "REJECTED", label: "已驳回", count: this.reviewCounts.REJECTED },
        { value: "", label: "全部", count: this.reviews.length }
      ]
    },
    riskReviewCount() {
      return this.reviews.filter(item => item.status === "PENDING" && this.countRisks(item) > 0).length
    },
    selectedRiskCount() {
      return this.selectedReview ? this.countRisks(this.selectedReview) : 0
    },
    selectedPassedCount() {
      if (!this.selectedReview) {
        return 0
      }
      return this.selectedReview.reviewChecks.length - this.selectedRiskCount
    },
    queueSummary() {
      const labels = {
        PENDING: "待审核商品，按提交时间排序",
        APPROVED: "查看已通过的审核记录",
        REJECTED: "查看已驳回的审核记录"
      }
      return labels[this.filters.status] || "全部审核记录"
    }
  },
  created() {
    this.loadReviews()
  },
  methods: {
    async loadReviews() {
      this.loading = true
      this.sourceMessage = ""
      try {
        const res = await fetchProductReviews()
        const data = res.data || {}
        if (Array.isArray(data.categoryOptions) && data.categoryOptions.length > 0) {
          this.categoryOptions = data.categoryOptions
        }
        this.reviews = Array.isArray(data.records) ? data.records.map(this.normalizeReview) : []
        this.usingFallbackData = false
      } catch (error) {
        this.reviews = this.createFallbackReviews()
        this.usingFallbackData = true
        this.sourceMessage = "商品审核接口暂不可用，当前操作仅在本页演示，不会写入服务器。"
      } finally {
        this.loading = false
        this.applyFilters()
      }
    },
    createFallbackReviews() {
      return [
        this.normalizeReview({
          id: 8101,
          title: "杜比亚中号 100只",
          storeName: "爬宠补给站上海仓",
          categoryCode: "live-feed",
          priceText: "¥39",
          deliveryMode: "HYBRID",
          status: "PENDING",
          submittedAt: "2026-06-22 14:10",
          description: "适合豹纹守宫和鬃狮的高频补货，支持同城和快递。",
          imageUrl: "https://images.unsplash.com/photo-1510130387422-82bed34b37e9?auto=format&fit=crop&w=900&q=80",
          tags: ["活饵", "同城可送", "高频补货"],
          reviewChecks: [
            { label: "类目匹配", desc: "商品类目与商家主营频道一致。", passed: true },
            { label: "定价合理", desc: "与平台同类商品相比处于正常区间。", passed: true },
            { label: "图片规范", desc: "主图清晰，但建议补 1 张细节图。", passed: false }
          ]
        }),
        this.normalizeReview({
          id: 8102,
          title: "钙粉基础装",
          storeName: "异宠营养仓",
          categoryCode: "supplement",
          priceText: "¥22",
          deliveryMode: "NATIONWIDE",
          status: "PENDING",
          submittedAt: "2026-06-22 11:40",
          description: "适合守宫和鬃狮基础补充，建议搭配维生素周期使用。",
          imageUrl: "https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?auto=format&fit=crop&w=900&q=80",
          tags: ["补剂", "全国可发", "新手友好"],
          reviewChecks: [
            { label: "类目匹配", desc: "补剂频道匹配正常。", passed: true },
            { label: "定价合理", desc: "建议补充净含量和规格。", passed: false },
            { label: "履约方式", desc: "支持全国快递。", passed: true }
          ]
        }),
        this.normalizeReview({
          id: 8103,
          title: "守宫周补套餐",
          storeName: "守宫周补工坊",
          categoryCode: "bundle",
          priceText: "¥88",
          deliveryMode: "SAME_CITY",
          status: "REJECTED",
          submittedAt: "2026-06-21 18:20",
          description: "杜比亚、钙粉和维生素组合，适合一周补货。",
          imageUrl: "https://images.unsplash.com/photo-1582719508461-905c673771fd?auto=format&fit=crop&w=900&q=80",
          tags: ["套餐", "守宫推荐", "同城优先"],
          reviewChecks: [
            { label: "类目匹配", desc: "套餐频道正常。", passed: true },
            { label: "规格完整", desc: "需拆分单品构成和规格。", passed: false },
            { label: "图片规范", desc: "主图不够清晰。", passed: false }
          ],
          reviewNote: "套餐规格描述不足。",
          rejectReason: "请补充套餐内各单品规格和更清晰主图。"
        })
      ]
    },
    normalizeReview(item) {
      const categoryCode = item.categoryCode || ""
      const status = item.status || "PENDING"
      const deliveryMode = item.deliveryMode || "SAME_CITY"
      return {
        id: item.id,
        title: item.title || "未命名商品",
        storeName: item.storeName || "未关联商家",
        categoryCode,
        categoryName: item.categoryName || this.resolveCategoryName(categoryCode),
        priceText: item.priceText || "¥0",
        deliveryMode,
        deliveryLabel: item.deliveryLabel || this.resolveDeliveryLabel(deliveryMode),
        status,
        statusLabel: this.resolveStatusLabel(status),
        submittedAt: item.submittedAt || "--",
        description: item.description || "暂无商品简介",
        imageUrl: item.imageUrl || "",
        tags: Array.isArray(item.tags) ? item.tags : [],
        reviewChecks: Array.isArray(item.reviewChecks) ? item.reviewChecks : [],
        reviewNote: item.reviewNote || "",
        rejectReason: item.rejectReason || ""
      }
    },
    resolveCategoryName(code) {
      const matched = this.categoryOptions.find(item => item.code === code)
      return matched ? matched.name : "未分类"
    },
    resolveStatusLabel(status) {
      switch (status) {
        case "APPROVED":
          return "已通过"
        case "REJECTED":
          return "已驳回"
        default:
          return "待审核"
      }
    },
    resolveStatusClass(status) {
      switch (status) {
        case "APPROVED":
          return "status-pill--success"
        case "REJECTED":
          return "status-pill--danger"
        default:
          return "status-pill--warning"
      }
    },
    resolveStatusDotClass(status) {
      switch (status) {
        case "APPROVED":
          return "status-dot--success"
        case "REJECTED":
          return "status-dot--danger"
        default:
          return "status-dot--warning"
      }
    },
    resolveDeliveryLabel(mode) {
      switch (mode) {
        case "NATIONWIDE":
          return "全国快递"
        case "HYBRID":
          return "同城 + 快递"
        case "APPOINTMENT":
          return "预约到店"
        default:
          return "同城履约"
      }
    },
    countRisks(item) {
      return item.reviewChecks.filter(check => !check.passed).length
    },
    formatSubmittedAt(value) {
      if (!value || value === "--") {
        return "时间未知"
      }
      const parts = value.split(" ")
      return parts.length > 1 ? parts[0].slice(5) + " " + parts[1] : value
    },
    setStatusFilter(status) {
      this.filters.status = status
      this.applyFilters()
    },
    applyFilters() {
      const keyword = this.filters.keyword.trim().toLowerCase()
      this.filteredReviews = this.reviews.filter(item => {
        const matchedStatus = !this.filters.status || item.status === this.filters.status
        const matchedCategory = !this.filters.categoryCode || item.categoryCode === this.filters.categoryCode
        const matchedKeyword = !keyword
          || item.title.toLowerCase().includes(keyword)
          || item.storeName.toLowerCase().includes(keyword)
        return matchedStatus && matchedCategory && matchedKeyword
      })

      if (!this.filteredReviews.length) {
        this.selectedReview = null
        this.decisionForm = createEmptyDecisionForm()
        return
      }

      const matched = this.selectedReview
        ? this.filteredReviews.find(item => item.id === this.selectedReview.id)
        : null
      if (!matched) {
        this.selectReview(this.filteredReviews[0])
      }
    },
    selectReview(item) {
      this.selectedReview = item
      this.decisionForm = {
        reviewNote: item.reviewNote || "",
        rejectReason: item.rejectReason || ""
      }
      this.actionState = ""
      this.actionError = false
    },
    applyRejectReason(reason) {
      const current = this.decisionForm.rejectReason.trim()
      if (current.includes(reason)) {
        return
      }
      this.decisionForm.rejectReason = current ? `${current}；${reason}` : reason
      this.actionState = ""
      this.actionError = false
    },
    async approveCurrent() {
      if (this.selectedReview) {
        await this.submitDecision("approve")
      }
    },
    async rejectCurrent() {
      if (!this.selectedReview) {
        return
      }
      if (!this.decisionForm.rejectReason.trim()) {
        this.actionState = "驳回前请填写具体原因"
        this.actionError = true
        return
      }
      await this.submitDecision("reject")
    },
    async submitDecision(type) {
      if (!this.selectedReview) {
        return
      }
      this.submitting = true
      this.actionType = type
      this.actionState = ""
      this.actionError = false

      const payload = {
        reviewNote: this.decisionForm.reviewNote.trim(),
        rejectReason: type === "reject" ? this.decisionForm.rejectReason.trim() : ""
      }

      try {
        if (!this.usingFallbackData) {
          if (type === "approve") {
            await approveProductReview(this.selectedReview.id, payload)
          } else {
            await rejectProductReview(this.selectedReview.id, payload)
          }
        }
        this.updateLocalReview(type, payload)
        this.actionState = this.usingFallbackData
          ? "演示结果已更新，未写入服务器"
          : type === "approve" ? "审核已通过" : "商品已驳回"
      } catch (error) {
        this.actionState = "提交失败，审核结果未保存"
        this.actionError = true
      } finally {
        this.submitting = false
        this.actionType = ""
      }
    },
    updateLocalReview(type, payload) {
      if (!this.selectedReview) {
        return
      }
      const reviewedId = this.selectedReview.id
      const nextStatus = type === "approve" ? "APPROVED" : "REJECTED"
      this.reviews = this.reviews.map(item => {
        if (item.id !== reviewedId) {
          return item
        }
        return {
          ...item,
          status: nextStatus,
          statusLabel: this.resolveStatusLabel(nextStatus),
          reviewNote: payload.reviewNote,
          rejectReason: payload.rejectReason
        }
      })
      this.selectedReview = this.reviews.find(item => item.id === reviewedId) || null
      this.applyFilters()
    }
  }
}
</script>

<style scoped>
.review-page {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 100px);
  min-height: 600px;
  gap: 12px;
  color: #172033;
}

.review-toolbar,
.review-workbench {
  border: 1px solid #dfe5ec;
  border-radius: 8px;
  background: #ffffff;
}

.review-toolbar {
  flex-shrink: 0;
  padding: 14px 16px 12px;
}

.review-toolbar__summary {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 18px;
}

.review-toolbar__copy {
  flex: 1;
  min-width: 220px;
}

.review-toolbar__title {
  color: #111827;
  font-size: 17px;
  line-height: 24px;
  font-weight: 800;
}

.review-toolbar__desc {
  margin-top: 2px;
  color: #667085;
  font-size: 12px;
  line-height: 18px;
}

.review-metrics {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.review-metric {
  min-width: 72px;
  padding: 0 16px;
  border-left: 1px solid #e6eaf0;
}

.review-metric span,
.review-metric strong {
  display: block;
}

.review-metric span {
  color: #7b8494;
  font-size: 11px;
  line-height: 16px;
}

.review-metric strong {
  margin-top: 1px;
  color: #172033;
  font-size: 19px;
  line-height: 22px;
}

.review-metric .review-metric__risk {
  color: #c2410c;
}

.data-source {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  height: 28px;
  padding: 0 10px;
  border: 1px solid #b9e2c4;
  border-radius: 999px;
  background: #f1fbf4;
  color: #18733b;
  font-size: 11px;
  font-weight: 700;
}

.data-source--demo {
  border-color: #f3c98b;
  background: #fff8eb;
  color: #9a4b0d;
}

.data-source__dot {
  width: 6px;
  height: 6px;
  margin-right: 6px;
  border-radius: 50%;
  background: #22a45d;
}

.data-source--demo .data-source__dot {
  background: #e28522;
}

.source-notice {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  padding: 7px 10px;
  border-left: 3px solid #e28522;
  background: #fff9ef;
  color: #7a4a16;
  font-size: 12px;
  line-height: 18px;
}

.source-notice strong {
  flex-shrink: 0;
}

.filter-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
}

.status-tabs {
  display: inline-grid;
  grid-template-columns: repeat(4, minmax(74px, 1fr));
  flex: 1;
  max-width: 420px;
  min-width: 316px;
  height: 36px;
  padding: 3px;
  border: 1px solid #e1e6ed;
  border-radius: 7px;
  background: #f5f7fa;
}

.status-tab {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  gap: 5px;
  padding: 0 8px;
  border: none;
  border-radius: 5px;
  background: transparent;
  color: #657084;
  font-size: 12px;
  cursor: pointer;
}

.status-tab--active {
  background: #ffffff;
  color: #172033;
  box-shadow: 0 1px 3px rgba(23, 32, 51, 0.12);
  font-weight: 700;
}

.status-tab__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: #e8ecf2;
  color: #667085;
  font-size: 10px;
  line-height: 18px;
}

.status-tab--active .status-tab__count {
  background: #eaf1ff;
  color: #2559a7;
}

.filter-control,
.filter-search {
  display: flex;
  align-items: center;
  height: 36px;
  border: 1px solid #d7dee8;
  border-radius: 7px;
  background: #ffffff;
}

.filter-control--category {
  width: 142px;
  flex-shrink: 0;
}

.filter-control select {
  width: 100%;
  height: 100%;
  padding: 0 30px 0 10px;
  border: none;
  background: transparent;
  color: #344054;
  outline: none;
  font-size: 12px;
}

.filter-search {
  flex: 1;
  min-width: 150px;
  padding: 0 10px;
}

.filter-search__icon {
  position: relative;
  width: 12px;
  height: 12px;
  margin-right: 8px;
  border: 1.5px solid #8993a4;
  border-radius: 50%;
}

.filter-search__icon::after {
  content: "";
  position: absolute;
  right: -4px;
  bottom: -2px;
  width: 5px;
  height: 1.5px;
  background: #8993a4;
  transform: rotate(45deg);
}

.filter-search input {
  min-width: 0;
  width: 100%;
  border: none;
  background: transparent;
  color: #172033;
  outline: none;
  font-size: 12px;
}

.filter-search input::placeholder {
  color: #98a2b3;
}

.refresh-button {
  flex-shrink: 0;
  height: 36px;
  padding: 0 13px;
  border: 1px solid #cfd6df;
  border-radius: 7px;
  background: #ffffff;
  color: #344054;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.refresh-button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.review-workbench {
  display: grid;
  grid-template-columns: 320px minmax(0, 1fr);
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.queue-panel {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  border-right: 1px solid #dfe5ec;
  background: #fbfcfd;
}

.queue-panel__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  min-height: 62px;
  padding: 10px 12px 10px 14px;
  border-bottom: 1px solid #e5e9ef;
  background: #ffffff;
}

.queue-panel__title {
  color: #172033;
  font-size: 14px;
  line-height: 20px;
  font-weight: 800;
}

.queue-panel__desc {
  margin-top: 2px;
  color: #818b9b;
  font-size: 10px;
  line-height: 15px;
}

.queue-panel__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 24px;
  padding: 0 8px;
  border-radius: 12px;
  background: #eef1f5;
  color: #596579;
  font-size: 11px;
  font-weight: 800;
}

.review-list {
  flex: 1;
  min-height: 0;
  padding: 8px;
  overflow-y: auto;
}

.review-list::-webkit-scrollbar,
.detail-scroll::-webkit-scrollbar {
  width: 6px;
}

.review-list::-webkit-scrollbar-thumb,
.detail-scroll::-webkit-scrollbar-thumb {
  border-radius: 3px;
  background: #d6dce5;
}

.review-item {
  position: relative;
  display: flex;
  width: 100%;
  min-height: 88px;
  margin-bottom: 6px;
  padding: 10px;
  border: 1px solid transparent;
  border-radius: 7px;
  background: transparent;
  text-align: left;
  cursor: pointer;
}

.review-item:hover {
  background: #ffffff;
  border-color: #e1e6ed;
}

.review-item--active {
  border-color: #9eb9e8;
  background: #f1f6ff;
  box-shadow: inset 3px 0 0 #3268b8;
}

.review-item__image {
  width: 58px;
  height: 58px;
  flex-shrink: 0;
  margin-right: 10px;
  border-radius: 6px;
  background: #e8ecf1;
  object-fit: cover;
}

.review-item__content,
.review-item__topline,
.review-item__bottomline {
  display: flex;
  min-width: 0;
}

.review-item__content {
  flex: 1;
  flex-direction: column;
}

.review-item__topline {
  align-items: center;
}

.review-item__title {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  color: #172033;
  font-size: 13px;
  line-height: 18px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.status-dot {
  width: 7px;
  height: 7px;
  flex-shrink: 0;
  margin-left: 7px;
  border-radius: 50%;
}

.status-dot--warning {
  background: #e28522;
}

.status-dot--success {
  background: #2e9d5b;
}

.status-dot--danger {
  background: #d04444;
}

.review-item__store {
  margin-top: 2px;
  overflow: hidden;
  color: #687386;
  font-size: 11px;
  line-height: 16px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.review-item__bottomline {
  align-items: center;
  gap: 7px;
  margin-top: 5px;
  color: #8a93a2;
  font-size: 10px;
  line-height: 15px;
}

.review-item__bottomline strong {
  color: #b33d19;
  font-size: 12px;
}

.review-item__risk {
  position: absolute;
  right: 10px;
  bottom: 6px;
  color: #b45b1a;
  font-size: 9px;
  line-height: 12px;
}

.queue-state,
.queue-empty {
  display: flex;
  flex: 1;
  min-height: 180px;
  align-items: center;
  justify-content: center;
  color: #7b8494;
  font-size: 12px;
}

.queue-empty {
  flex-direction: column;
  gap: 5px;
  padding: 24px;
  text-align: center;
}

.queue-empty__mark {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  margin-bottom: 3px;
  border: 1px solid #d8dee7;
  border-radius: 50%;
  background: #ffffff;
  color: #9aa3b1;
  font-weight: 800;
}

.queue-empty strong {
  color: #344054;
  font-size: 13px;
}

.queue-empty span {
  font-size: 11px;
}

.review-detail {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  background: #ffffff;
}

.detail-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 62px;
  flex-shrink: 0;
  gap: 16px;
  padding: 9px 16px;
  border-bottom: 1px solid #e5e9ef;
}

.detail-header__copy {
  min-width: 0;
}

.detail-header__eyebrow {
  overflow: hidden;
  color: #7a8494;
  font-size: 10px;
  line-height: 15px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.detail-header__title-row {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 8px;
  margin-top: 2px;
}

.detail-header h2 {
  min-width: 0;
  margin: 0;
  overflow: hidden;
  color: #111827;
  font-size: 16px;
  line-height: 22px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  height: 22px;
  padding: 0 8px;
  border-radius: 11px;
  font-size: 10px;
  font-weight: 700;
}

.status-pill--warning {
  background: #fff2dd;
  color: #a55012;
}

.status-pill--success {
  background: #e9f8ee;
  color: #18733b;
}

.status-pill--danger {
  background: #fcebec;
  color: #ad3038;
}

.detail-progress {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  flex-shrink: 0;
  color: #7b8494;
  font-size: 10px;
  line-height: 15px;
}

.detail-progress strong {
  color: #b45b1a;
  font-size: 11px;
}

.detail-progress .detail-progress__passed {
  color: #18733b;
}

.detail-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.product-summary {
  display: grid;
  grid-template-columns: 138px minmax(0, 1fr);
  gap: 14px;
  padding: 14px 16px;
  border-bottom: 1px solid #e7ebf0;
  background: #fcfcfd;
}

.product-summary__image {
  width: 138px;
  height: 104px;
  border-radius: 7px;
  background: #e7ebf0;
  object-fit: cover;
}

.product-summary__body {
  min-width: 0;
}

.product-summary__line {
  display: flex;
  align-items: baseline;
  gap: 12px;
}

.product-summary__line > span {
  color: #667085;
  font-size: 11px;
}

.product-summary__price {
  color: #a93618;
  font-size: 22px;
  line-height: 28px;
  font-weight: 800;
}

.product-summary p {
  display: -webkit-box;
  margin: 5px 0 0;
  overflow: hidden;
  color: #4f5b6d;
  font-size: 12px;
  line-height: 19px;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.tag-row {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 8px;
}

.tag-pill {
  padding: 3px 7px;
  border: 1px solid #d8e1ef;
  border-radius: 4px;
  background: #f4f7fb;
  color: #456187;
  font-size: 9px;
  line-height: 13px;
}

.detail-columns {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 250px;
  min-height: 100%;
}

.detail-content {
  min-width: 0;
}

.detail-section {
  padding: 15px 16px;
  border-bottom: 1px solid #e7ebf0;
}

.detail-section--checks {
  border-bottom: none;
}

.section-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.section-heading h3 {
  margin: 0;
  color: #172033;
  font-size: 13px;
  line-height: 19px;
}

.section-heading p {
  margin: 2px 0 0;
  color: #8a93a2;
  font-size: 10px;
  line-height: 15px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 18px;
  margin: 13px 0 0;
}

.info-grid > div {
  min-width: 0;
}

.info-grid dt {
  color: #8a93a2;
  font-size: 10px;
  line-height: 15px;
}

.info-grid dd {
  margin: 2px 0 0;
  overflow-wrap: anywhere;
  color: #344054;
  font-size: 11px;
  line-height: 17px;
  font-weight: 700;
}

.risk-summary {
  flex-shrink: 0;
  padding: 3px 7px;
  border-radius: 4px;
  background: #fff2dd;
  color: #a55012;
  font-size: 9px;
  line-height: 14px;
  font-weight: 700;
}

.risk-summary--passed {
  background: #e9f8ee;
  color: #18733b;
}

.check-list {
  margin-top: 11px;
}

.check-row {
  display: flex;
  gap: 9px;
  padding: 8px 0;
  border-top: 1px solid #edf0f4;
}

.check-row:first-child {
  border-top: none;
}

.check-row__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  border-radius: 50%;
  font-size: 10px;
  line-height: 20px;
  font-weight: 800;
}

.check-row__icon--passed {
  background: #e8f7ee;
  color: #218049;
}

.check-row__icon--risk {
  background: #fff0dc;
  color: #b45b1a;
}

.check-row__title {
  color: #344054;
  font-size: 11px;
  line-height: 16px;
  font-weight: 700;
}

.check-row__desc {
  margin-top: 1px;
  color: #7b8494;
  font-size: 10px;
  line-height: 15px;
}

.decision-panel {
  min-width: 0;
  padding: 15px 14px;
  border-left: 1px solid #e7ebf0;
  background: #fafbfc;
}

.decision-field {
  display: block;
  margin-top: 14px;
}

.decision-field > span {
  display: block;
  color: #344054;
  font-size: 11px;
  line-height: 16px;
  font-weight: 700;
}

.decision-field small {
  color: #929baa;
  font-size: 9px;
  font-weight: 400;
}

.decision-field textarea {
  width: 100%;
  min-height: 72px;
  margin-top: 6px;
  padding: 8px 9px;
  border: 1px solid #d7dee8;
  border-radius: 6px;
  background: #ffffff;
  color: #172033;
  outline: none;
  resize: vertical;
  font-size: 11px;
  line-height: 17px;
}

.decision-field textarea:focus {
  border-color: #7396cd;
  box-shadow: 0 0 0 2px rgba(50, 104, 184, 0.1);
}

.decision-field textarea::placeholder {
  color: #a1a9b5;
}

.decision-field .decision-field__textarea--reject {
  min-height: 88px;
}

.reason-presets {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 7px;
}

.reason-presets button {
  padding: 4px 6px;
  border: 1px solid #d9dee6;
  border-radius: 4px;
  background: #ffffff;
  color: #667085;
  font-size: 9px;
  line-height: 14px;
  cursor: pointer;
}

.reason-presets button:hover {
  border-color: #9eabc0;
  color: #344054;
}

.decision-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 58px;
  flex-shrink: 0;
  gap: 14px;
  padding: 8px 12px 8px 16px;
  border-top: 1px solid #dfe5ec;
  background: #ffffff;
  box-shadow: 0 -5px 14px rgba(23, 32, 51, 0.04);
}

.decision-bar__state {
  display: flex;
  align-items: center;
  min-width: 0;
  color: #7b8494;
  font-size: 10px;
  line-height: 15px;
}

.decision-bar__indicator {
  width: 7px;
  height: 7px;
  flex-shrink: 0;
  margin-right: 7px;
  border-radius: 50%;
  background: #2e9d5b;
}

.decision-bar__indicator--error {
  background: #d04444;
}

.decision-bar__text--error {
  color: #b4232d;
}

.decision-bar__actions {
  display: flex;
  flex-shrink: 0;
  gap: 7px;
}

.decision-button {
  height: 34px;
  padding: 0 13px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
}

.decision-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.decision-button--reject {
  border: 1px solid #e0b8ba;
  background: #fffafa;
  color: #ad3038;
}

.decision-button--approve {
  border: 1px solid #245a9d;
  background: #2d65ab;
  color: #ffffff;
}

.review-detail--empty {
  align-items: center;
  justify-content: center;
  padding: 30px;
  color: #8a93a2;
  text-align: center;
}

.review-detail--empty h2 {
  margin: 12px 0 0;
  color: #344054;
  font-size: 16px;
}

.review-detail--empty p {
  margin: 5px 0 0;
  font-size: 11px;
}

.detail-empty__mark {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: 1px solid #d7dee8;
  border-radius: 50%;
  background: #f8fafc;
  color: #788496;
  font-weight: 800;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@media (max-width: 1180px) {
  .review-page {
    height: auto;
    min-height: 0;
  }

  .review-toolbar__summary {
    flex-wrap: wrap;
  }

  .review-toolbar__copy {
    flex-basis: calc(100% - 310px);
  }

  .data-source {
    margin-left: auto;
  }

  .filter-bar {
    flex-wrap: wrap;
  }

  .status-tabs {
    max-width: none;
    flex-basis: 100%;
  }

  .review-workbench {
    grid-template-columns: 1fr;
    overflow: visible;
  }

  .queue-panel {
    max-height: 390px;
    border-right: none;
    border-bottom: 1px solid #dfe5ec;
  }

  .review-detail {
    min-height: 620px;
  }
}

@media (max-width: 860px) {
  .review-toolbar {
    padding: 12px;
  }

  .review-toolbar__summary {
    gap: 12px;
  }

  .review-toolbar__copy {
    flex-basis: 100%;
  }

  .review-metrics {
    flex: 1;
  }

  .review-metric {
    min-width: 58px;
    padding: 0 10px;
  }

  .filter-control--category {
    flex: 1;
    width: auto;
  }

  .filter-search {
    flex-basis: 100%;
    order: 3;
  }

  .detail-columns {
    grid-template-columns: 1fr;
  }

  .decision-panel {
    border-top: 1px solid #e7ebf0;
    border-left: none;
  }
}

@media (max-width: 640px) {
  .review-toolbar__desc,
  .source-notice span,
  .decision-bar__state {
    display: none;
  }

  .source-notice {
    display: inline-flex;
  }

  .status-tabs {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    height: auto;
    min-width: 0;
  }

  .status-tab {
    height: 30px;
  }

  .product-summary {
    grid-template-columns: 94px minmax(0, 1fr);
    padding: 12px;
  }

  .product-summary__image {
    width: 94px;
    height: 94px;
  }

  .info-grid {
    grid-template-columns: 1fr;
  }

  .detail-header {
    padding: 9px 12px;
  }

  .detail-progress {
    display: none;
  }

  .decision-bar {
    justify-content: flex-end;
  }

  .decision-bar__actions {
    width: 100%;
  }

  .decision-button {
    flex: 1;
  }
}
</style>
